"use client";

import { c as _c } from "react/compiler-runtime";
import { useEditorState } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import { Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Code, Minus, Undo2, Redo2 } from "lucide-react";

// Compact formatting toolbar for the entry drawer's lore editor. Mirrors the
// chapter editor toolbar (editor-toolbar.tsx) but trimmed to the marks/nodes the
// drawer's StarterKit config actually enables (headings limited to H2/H3).

interface EntryEditorToolbarProps {
  editor: Editor | null;
}
export function EntryEditorToolbar(t0) {
  const $ = _c(96);
  const {
    editor
  } = t0;
  let t1;
  if ($[0] !== editor) {
    t1 = {
      editor,
      selector: _temp
    };
    $[0] = editor;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const editorState = useEditorState(t1);
  if (!editor || !editorState) {
    return null;
  }
  let t2;
  if ($[2] !== editor) {
    t2 = () => editor.chain().focus().toggleBold().run();
    $[2] = editor;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Bold className="h-4 w-4" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== editorState.isBold || $[6] !== t2) {
    t4 = <ToolbarButton onClick={t2} active={editorState.isBold} title={"Bold (\u2318B)"} icon={t3} />;
    $[5] = editorState.isBold;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== editor) {
    t5 = () => editor.chain().focus().toggleItalic().run();
    $[8] = editor;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Italic className="h-4 w-4" />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== editorState.isItalic || $[12] !== t5) {
    t7 = <ToolbarButton onClick={t5} active={editorState.isItalic} title={"Italic (\u2318I)"} icon={t6} />;
    $[11] = editorState.isItalic;
    $[12] = t5;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== editor) {
    t8 = () => editor.chain().focus().toggleUnderline().run();
    $[14] = editor;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Underline className="h-4 w-4" />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== editorState.isUnderline || $[18] !== t8) {
    t10 = <ToolbarButton onClick={t8} active={editorState.isUnderline} title={"Underline (\u2318U)"} icon={t9} />;
    $[17] = editorState.isUnderline;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== editor) {
    t11 = () => editor.chain().focus().toggleStrike().run();
    $[20] = editor;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Strikethrough className="h-4 w-4" />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== editorState.isStrike || $[24] !== t11) {
    t13 = <ToolbarButton onClick={t11} active={editorState.isStrike} title="Strikethrough" icon={t12} />;
    $[23] = editorState.isStrike;
    $[24] = t11;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Divider />;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== editor) {
    t15 = () => editor.chain().focus().toggleHeading({
      level: 2
    }).run();
    $[27] = editor;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Heading2 className="h-4 w-4" />;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== editorState.isH2 || $[31] !== t15) {
    t17 = <ToolbarButton onClick={t15} active={editorState.isH2} title="Heading 2" icon={t16} />;
    $[30] = editorState.isH2;
    $[31] = t15;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== editor) {
    t18 = () => editor.chain().focus().toggleHeading({
      level: 3
    }).run();
    $[33] = editor;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Heading3 className="h-4 w-4" />;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== editorState.isH3 || $[37] !== t18) {
    t20 = <ToolbarButton onClick={t18} active={editorState.isH3} title="Heading 3" icon={t19} />;
    $[36] = editorState.isH3;
    $[37] = t18;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Divider />;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== editor) {
    t22 = () => editor.chain().focus().toggleBulletList().run();
    $[40] = editor;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <List className="h-4 w-4" />;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== editorState.isBulletList || $[44] !== t22) {
    t24 = <ToolbarButton onClick={t22} active={editorState.isBulletList} title="Bullet list" icon={t23} />;
    $[43] = editorState.isBulletList;
    $[44] = t22;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== editor) {
    t25 = () => editor.chain().focus().toggleOrderedList().run();
    $[46] = editor;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ListOrdered className="h-4 w-4" />;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== editorState.isOrderedList || $[50] !== t25) {
    t27 = <ToolbarButton onClick={t25} active={editorState.isOrderedList} title="Numbered list" icon={t26} />;
    $[49] = editorState.isOrderedList;
    $[50] = t25;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== editor) {
    t28 = () => editor.chain().focus().toggleBlockquote().run();
    $[52] = editor;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Quote className="h-4 w-4" />;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== editorState.isBlockquote || $[56] !== t28) {
    t30 = <ToolbarButton onClick={t28} active={editorState.isBlockquote} title="Block quote" icon={t29} />;
    $[55] = editorState.isBlockquote;
    $[56] = t28;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== editor) {
    t31 = () => editor.chain().focus().toggleCode().run();
    $[58] = editor;
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  let t32;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Code className="h-4 w-4" />;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  let t33;
  if ($[61] !== editorState.isCode || $[62] !== t31) {
    t33 = <ToolbarButton onClick={t31} active={editorState.isCode} title="Inline code" icon={t32} />;
    $[61] = editorState.isCode;
    $[62] = t31;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== editor) {
    t34 = () => editor.chain().focus().setHorizontalRule().run();
    $[64] = editor;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Minus className="h-4 w-4" />;
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[67] !== t34) {
    t36 = <ToolbarButton onClick={t34} title="Horizontal rule" icon={t35} />;
    $[67] = t34;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Divider />;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] !== editor) {
    t38 = () => editor.chain().focus().undo().run();
    $[70] = editor;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  const t39 = !editorState.canUndo;
  let t40;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Undo2 className="h-4 w-4" />;
    $[72] = t40;
  } else {
    t40 = $[72];
  }
  let t41;
  if ($[73] !== t38 || $[74] !== t39) {
    t41 = <ToolbarButton onClick={t38} disabled={t39} title={"Undo (\u2318Z)"} icon={t40} />;
    $[73] = t38;
    $[74] = t39;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  let t42;
  if ($[76] !== editor) {
    t42 = () => editor.chain().focus().redo().run();
    $[76] = editor;
    $[77] = t42;
  } else {
    t42 = $[77];
  }
  const t43 = !editorState.canRedo;
  let t44;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Redo2 className="h-4 w-4" />;
    $[78] = t44;
  } else {
    t44 = $[78];
  }
  let t45;
  if ($[79] !== t42 || $[80] !== t43) {
    t45 = <ToolbarButton onClick={t42} disabled={t43} title={"Redo (\u2318\u21E7Z)"} icon={t44} />;
    $[79] = t42;
    $[80] = t43;
    $[81] = t45;
  } else {
    t45 = $[81];
  }
  let t46;
  if ($[82] !== t10 || $[83] !== t13 || $[84] !== t17 || $[85] !== t20 || $[86] !== t24 || $[87] !== t27 || $[88] !== t30 || $[89] !== t33 || $[90] !== t36 || $[91] !== t4 || $[92] !== t41 || $[93] !== t45 || $[94] !== t7) {
    t46 = <div className="flex flex-wrap items-center gap-0.5 rounded-t-lg border border-b-0 border-border/50 bg-card/60 px-1.5 py-1">{t4}{t7}{t10}{t13}{t14}{t17}{t20}{t21}{t24}{t27}{t30}{t33}{t36}{t37}{t41}{t45}</div>;
    $[82] = t10;
    $[83] = t13;
    $[84] = t17;
    $[85] = t20;
    $[86] = t24;
    $[87] = t27;
    $[88] = t30;
    $[89] = t33;
    $[90] = t36;
    $[91] = t4;
    $[92] = t41;
    $[93] = t45;
    $[94] = t7;
    $[95] = t46;
  } else {
    t46 = $[95];
  }
  return t46;
}
function _temp(t0) {
  const {
    editor: e
  } = t0;
  return {
    isBold: e?.isActive("bold") ?? false,
    isItalic: e?.isActive("italic") ?? false,
    isUnderline: e?.isActive("underline") ?? false,
    isStrike: e?.isActive("strike") ?? false,
    isH2: e?.isActive("heading", {
      level: 2
    }) ?? false,
    isH3: e?.isActive("heading", {
      level: 3
    }) ?? false,
    isBulletList: e?.isActive("bulletList") ?? false,
    isOrderedList: e?.isActive("orderedList") ?? false,
    isBlockquote: e?.isActive("blockquote") ?? false,
    isCode: e?.isActive("code") ?? false,
    canUndo: e?.can().undo() ?? false,
    canRedo: e?.can().redo() ?? false
  };
}
function Divider() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mx-1 h-5 w-px bg-border/60" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ToolbarButton(t0) {
  const $ = _c(6);
  const {
    icon,
    onClick,
    active,
    disabled,
    title
  } = t0;
  const t1 = `flex h-8 w-8 items-center justify-center rounded-sm transition-colors sm:h-7 sm:w-7 ${active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"} ${disabled ? "cursor-not-allowed opacity-30" : "cursor-pointer"}`;
  let t2;
  if ($[0] !== disabled || $[1] !== icon || $[2] !== onClick || $[3] !== t1 || $[4] !== title) {
    t2 = <button type="button" onClick={onClick} disabled={disabled} title={title} className={t1}>{icon}</button>;
    $[0] = disabled;
    $[1] = icon;
    $[2] = onClick;
    $[3] = t1;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}